import { Box, Field as ChakraField, HStack, Input, InputGroup, Text } from '@chakra-ui/react';
import { GPSCoordinatesInputProps } from '@components/ui/coordinatesInput/GPSCoordinates.slots';
import { useGPSCoordinatesFieldContext } from '@components/ui/coordinatesInput/GPSCoordinatesFieldContext';
import { toPrecision } from '@utils/math.utils';
import { dmsToString } from '@utils/validation/coordinates.utils';
import DmsCoordinates, { parseDms } from 'dms-conversion';
import { Field, FieldInputProps, FormikProps, getIn, useFormikContext } from 'formik';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { CoordinatesUnitEnum } from '@/types/commons/commons.types';

import { InputNumber } from '../input/InputNumber';

export type DMS = Record<'degrees' | 'minutes' | 'seconds', string | number | null>;

export const LatLongInput = ({ type, name, readOnly }: GPSCoordinatesInputProps) => {
  const { setFieldValue } = useFormikContext<unknown>();

  const { unit, lat, long } = useGPSCoordinatesFieldContext();
  const isLatitude = type === 'latitude';
  const value = isLatitude ? lat : long;
  const numberValue = value ? Number(value) : null;
  const [dms, setDms] = useState<DMS>({
    degrees: null,
    minutes: null,
    seconds: null,
  });

  useEffect(() => {
    if (numberValue != null && !isNaN(numberValue)) {
      try {
        const dmsCoords = isLatitude ? new DmsCoordinates(numberValue, 0) : new DmsCoordinates(0, numberValue);
        const dmsArray = isLatitude ? dmsCoords.dmsArrays.latitude : dmsCoords.dmsArrays.longitude;
        const [degrees, minutes, seconds] = dmsArray;

        setDms({ degrees, minutes, seconds: toPrecision(Number(seconds), 3) });
      } catch (err) {
        console.warn('Invalid coordinate:', err);
      }
    }
  }, [isLatitude, numberValue, type]);

  const handleChange = (field: 'degrees' | 'minutes' | 'seconds', newValue: string | number | null) => {
    if (newValue === null || newValue === '') {
      newValue = null;
    }

    const updated: DMS = { ...dms, [field]: newValue };

    setDms(updated);
    setFieldValue(`${name}.${type}`, parseDms(dmsToString(updated)).toString());
  };

  if (unit === CoordinatesUnitEnum.DECIMAL_DEGREES) {
    return (
      <Field name={`${name}.${type}`}>
        {({ field, form }: { field: FieldInputProps<string>; form: FormikProps<unknown> }) => {
          const error = getIn(form.errors, field.name);
          const touched = getIn(form.touched, field.name);

          return (
            <ChakraField.Root invalid={!!touched && !!error}>
              <InputGroup
                className="group"
                paddingY={0.25}
                gap={1}
                startElement={
                  <Text color="neutral.300">
                    <FormattedMessage id={`global.${type}ShortHand`} />
                  </Text>
                }
                startElementProps={{ width: 'auto', justifyContent: 'flex-start', minWidth: '34px', paddingX: 2 }}
                endElement={
                  <Text color="neutral.300" textTransform="capitalize">
                    {isLatitude ? 'N' : 'E'}
                  </Text>
                }
                endElementProps={{ paddingInline: 2 }}
              >
                <InputNumber
                  name={type}
                  id={type}
                  value={field.value}
                  readOnly={readOnly}
                  style={{
                    paddingInlineStart: '56px',
                    paddingInlineEnd: '30px',
                  }}
                  onChange={(e) => form.setFieldValue(field.name, e)}
                  onBlur={() => form.setFieldTouched(field.name, true)}
                />
              </InputGroup>
            </ChakraField.Root>
          );
        }}
      </Field>
    );
  }

  return (
    <Field name={`${name}.${type}`}>
      {({ field, form }: { field: FieldInputProps<string>; form: FormikProps<unknown> }) => {
        const error = getIn(form.errors, field.name);
        const touched = getIn(form.touched, field.name);
        const invalid = !!touched && !!error;

        return (
          <ChakraField.Root invalid={invalid}>
            <HStack
              className="group"
              backgroundColor={invalid ? 'red.800' : 'neutral.800'}
              border="2px solid"
              borderColor={invalid ? 'red' : 'neutral.black'}
              paddingX={2}
              paddingY={0.25}
              gap={0.5}
              width="100%"
              _focus={{ borderColor: 'skyBlue' }}
              _hover={{ borderColor: 'skyBlue' }}
            >
              <Box display="flex" width="auto" minWidth="34px">
                <Text color="neutral.300">
                  <FormattedMessage id={`global.${type}ShortHand`} />
                </Text>
              </Box>
              <HStack flex={1} gap={0.5}>
                <Input
                  type="number"
                  variant="unstyled"
                  width="auto"
                  minWidth="20px"
                  maxWidth="50px"
                  min={isLatitude ? -90 : -180}
                  max={isLatitude ? 90 : 180}
                  value={dms.degrees ?? ''}
                  readOnly={readOnly}
                  onChange={(e) => handleChange('degrees', e.target.value)}
                  onBlur={() => form.setFieldTouched(field.name, true)}
                />
                <Text color="neutral.300">°</Text>
                <Input
                  type="number"
                  variant="unstyled"
                  width="auto"
                  minWidth="20px"
                  maxWidth="50px"
                  min={0}
                  max={59}
                  readOnly={readOnly}
                  value={dms.minutes ?? ''}
                  onChange={(e) => handleChange('minutes', e.target.value)}
                  onBlur={() => form.setFieldTouched(field.name, true)}
                />
                <Text color="neutral.300">&#39;</Text>
                <Input
                  type="number"
                  variant="unstyled"
                  width="auto"
                  minWidth="20px"
                  maxWidth="110px"
                  min={0}
                  max={60}
                  step="0.00001"
                  value={dms.seconds ?? ''}
                  readOnly={readOnly}
                  onChange={(e) => {
                    const value = e.target.value;
                    const isValidDecimal = /^(\d+(\.\d*)?)?$/.test(value);

                    if (isValidDecimal) {
                      setDms((prev) => ({ ...prev, seconds: value }));
                    }
                  }}
                  onBlur={() => form.setFieldTouched(field.name, true)}
                />
                <Text color="neutral.300">&#34;</Text>
              </HStack>
              <Text color="neutral.300" textTransform="capitalize">
                {isLatitude ? 'N' : 'E'}
              </Text>
            </HStack>
          </ChakraField.Root>
        );
      }}
    </Field>
  );
};
